import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { useAdminStore } from "@/store";
import { AdminMfaChallenge } from "@/components/admin-mfa-challenge";

export const Route = createFileRoute("/login")({
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated && context.auth.user?.role === "super_admin") {
      throw redirect({ to: "/" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const setAuth = useAdminStore((s) => s.setAuth);
  const clearAuth = useAdminStore((s) => s.clearAuth);
  // The portal name lives in the shared common namespace so a rebrand moves
  // the heading and the restricted-access notice with it.
  const appName = t("common:appName");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // MFA challenge state
  const [mfaSessionToken, setMfaSessionToken] = useState<string | null>(null);
  const [mfaMethods, setMfaMethods] = useState<string[]>([]);

  async function completeLogin(data: {
    access_token: string;
    refresh_token: string;
    user: { id: string; email: string; role: string; email_verified: boolean };
  }) {
    const user = {
      id: data.user.id,
      email: data.user.email,
      role: data.user.role,
      emailVerified: data.user.email_verified,
    };

    if (user.role !== "super_admin") {
      toast.error(t("auth:admin.accessDenied"));
      clearAuth();
      return;
    }

    setAuth(data.access_token, user, data.refresh_token);
    toast.success(t("auth:admin.welcomeBack"));
    await navigate({ to: "/" });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!email || !password) {
      toast.error(t("auth:admin.missingCredentials"));
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await apiClient.POST("/auth/login", {
        body: { email, password },
      });

      if (error) {
        toast.error(t("auth:invalidCredentials"));
        return;
      }

      if (!data) {
        toast.error(t("auth:admin.unexpectedError"));
        return;
      }

      // MFA required -- show challenge form
      if (data.mfa_required && data.mfa_session_token && data.mfa_methods) {
        setMfaSessionToken(data.mfa_session_token);
        setMfaMethods(data.mfa_methods);
        return;
      }

      if (!data.user || !data.access_token || !data.refresh_token) {
        toast.error(t("auth:admin.unexpectedResponse"));
        return;
      }

      await completeLogin(
        data as {
          access_token: string;
          refresh_token: string;
          user: { id: string; email: string; role: string; email_verified: boolean };
        },
      );
    } catch {
      toast.error(t("auth:admin.networkError"));
    } finally {
      setIsLoading(false);
    }
  }

  // MFA challenge screen
  if (mfaSessionToken) {
    return (
      <AdminMfaChallenge
        mfaSessionToken={mfaSessionToken}
        mfaMethods={mfaMethods}
        completeLogin={completeLogin}
        onCancel={() => {
          setMfaSessionToken(null);
          setMfaMethods([]);
        }}
      />
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("auth:admin.heading", { appName })}
          </h1>
          <p className="mt-2 text-sm text-gray-500">{t("auth:admin.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700">
              {t("auth:admin.emailLabel")}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              placeholder={t("auth:admin.emailPlaceholder")}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-gray-700">
              {t("auth:passwordLabel")}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              placeholder={t("auth:admin.passwordPlaceholder")}
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-hover focus:ring-2 focus:ring-primary/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? t("auth:signingIn") : t("auth:signInLink")}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-gray-400">
          {t("auth:admin.restrictedNotice", { appName })}
        </p>
      </div>
    </div>
  );
}
